import * as React from 'react';
import { Badge, Button } from '../ui.jsx';
import { Icon } from '../icons.jsx';

const SCENARIOS = [
  {
    id: 'availability',
    label: 'Update openings',
    icon: 'spark',
    intro: '3 Pre-K openings',
    messages: [
      { from: 'provider', text: '3 Pre-K openings' },
      { from: 'assistant', text: 'Full-time, part-time, or both?' },
      { from: 'provider', text: 'Full-time, Oct 15' },
      {
        from: 'assistant',
        text: 'Confirm: Set Pre-K to 3 full-time openings starting Oct 15. Reply YES to publish, EDIT to change, or CANCEL.',
      },
      { from: 'provider', text: 'YES' },
      { from: 'assistant', text: 'Published. Families can now see 3 Pre-K openings.' },
    ],
  },
  {
    id: 'tour',
    label: 'Confirm a tour',
    icon: 'calendar-check',
    intro: 'Reply CONFIRM',
    messages: [
      {
        from: 'assistant',
        text: 'New tour request: Maya R., Pre-K, Thu Oct 1 at 10 AM, in person. Reply CONFIRM, CHANGE, or DECLINE.',
      },
      { from: 'provider', text: 'CONFIRM' },
      { from: 'assistant', text: 'Confirmed. Maya will receive the tour details.' },
    ],
  },
  {
    id: 'waitlist',
    label: 'Contact waitlist',
    icon: 'inbox',
    intro: 'NEXT INFANT',
    messages: [
      { from: 'provider', text: 'NEXT INFANT' },
      {
        from: 'assistant',
        text: 'Nguyen family is next (joined Sep 22). Draft: “Sunnyside may have an infant opening. Reply YES if you want the center to contact you.” Reply SEND, EDIT, or CANCEL.',
      },
      { from: 'provider', text: 'SEND' },
      { from: 'assistant', text: 'Sent. Nguyen family is marked as contacted.' },
    ],
  },
  {
    id: 'hours',
    label: 'Change hours',
    icon: 'bell',
    intro: 'Closing at 4 Friday',
    messages: [
      { from: 'provider', text: 'Closing at 4 Friday' },
      {
        from: 'assistant',
        text: 'Confirm: Show “Closing at 4 PM” for Friday, Oct 2. Reply YES to publish, EDIT to change, or CANCEL.',
      },
      { from: 'provider', text: 'YES' },
      { from: 'assistant', text: 'Published. Families can now see the special hours notice.' },
    ],
  },
];

const PhoneMessage = ({ message }) => (
  <div className={`sms-bubble-row is-${message.from}`}>
    <div className={`sms-bubble is-${message.from}`}>
      {message.image && <img src={message.image} alt="Garden activity to publish"/>}
      <span>{message.text}</span>
    </div>
  </div>
);

export const TextDemoScreen = ({ go }) => {
  const [scenarioId, setScenarioId] = React.useState('availability');
  const scenario = SCENARIOS.find(item => item.id === scenarioId) || SCENARIOS[0];

  return (
    <div className="text-demo route-in">
      <section className="text-demo-hero">
        <div className="container">
          <Badge tone="yellow">Faithful standard SMS mock</Badge>
          <h1>What the provider’s real text conversation could look like</h1>
          <p>
            SMS stays simple: plain text, short reply words, and links back to the app when a task
            needs richer review.
          </p>
          <div>
            <Button variant="secondary" icon="grid" onClick={() => go('assistant')}>
              Open the in-app chat
            </Button>
            <Button variant="ghost" onClick={() => go('dashboard')}>
              Open provider dashboard
            </Button>
          </div>
        </div>
      </section>

      <section className="text-demo-workspace">
        <div className="container">
          <nav className="text-scenarios" aria-label="Text message examples">
            <span>Choose a provider task</span>
            {SCENARIOS.map(item => (
              <button
                key={item.id}
                type="button"
                className={scenarioId === item.id ? 'is-active' : ''}
                onClick={() => setScenarioId(item.id)}
                aria-current={scenarioId === item.id ? 'page' : undefined}
              >
                <i><Icon name={item.icon} size={17}/></i>
                <span>
                  <strong>{item.label}</strong>
                  <small>“{item.intro}”</small>
                </span>
                <Icon name="chev-right" size={15}/>
              </button>
            ))}
            <div className="text-demo-note">
              <Icon name="shield" size={16}/>
              <span>There are no buttons or rich cards inside a standard SMS conversation.</span>
            </div>
            <div className="text-capability-split">
              <div>
                <strong>Good fit for SMS</strong>
                <span>Openings, weekly checks, tour replies, waitlist outreach, and special hours.</span>
              </div>
              <div>
                <strong>Better in the app</strong>
                <span>Photos, editable captions, detailed family records, galleries, and complex changes.</span>
              </div>
            </div>
          </nav>

          <div className="phone-stage">
            <div className="phone-shell">
              <div className="phone-speaker"/>
              <div className="phone-screen">
                <header>
                  <button type="button" aria-label="Back"><Icon name="chev-left" size={18}/></button>
                  <span className="phone-contact-logo"><Icon name="spark" size={17}/></span>
                  <div>
                    <strong>Find My Childcare</strong>
                    <small>Text Assistant</small>
                  </div>
                  <button type="button" aria-label="Information">
                    <Icon name="shield" size={17}/>
                  </button>
                </header>
                <div className="sms-thread" aria-live="polite">
                  <time>Today, 10:42 AM</time>
                  {scenario.messages.map((message, index) => (
                    <PhoneMessage key={`${scenario.id}-${index}`} message={message}/>
                  ))}
                </div>
                <div className="sms-standard-note">
                  <Icon name="phone" size={15}/>
                  <span>Standard SMS conversation</span>
                </div>
                <div className="sms-composer">
                  <span>SMS text message</span>
                  <Icon name="arrow-right" size={17}/>
                </div>
              </div>
            </div>
            <p>This is a faithful visual mock. Real SMS delivery and replies are not connected yet.</p>
          </div>
        </div>
      </section>
    </div>
  );
};
